import { useEffect, useState, useCallback, Fragment } from "react";
import api, { fmtEUR, fmtNum } from "@/lib/api";
import { monthOptions, monthRangeFor } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, Wallet, Percent, Landmark, CreditCard, ChevronDown, ChevronRight } from "lucide-react";

const GROUP_LABELS = {
  coste_ventas: "Coste de ventas", ocupacion: "Ocupación", suministros: "Suministros",
  marketing: "Marketing", operaciones: "Operaciones", administracion: "Administración",
  financieros: "Gastos financieros", personal: "Personal", impuestos: "Impuestos", otro: "Otros",
  otros_ingresos: "Otros ingresos",
};

const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function monthRange(offset = 0) {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  return [isoLocal(first), isoLocal(last)];
}

function presetRange(preset) {
  const now = new Date();
  if (preset === "mes") return monthRange(0);
  if (preset === "mes_anterior") return monthRange(-1);
  if (preset === "3m") {
    return [isoLocal(new Date(now.getFullYear(), now.getMonth() - 2, 1)), isoLocal(now)];
  }
  if (preset === "ytd") return [isoLocal(new Date(now.getFullYear(), 0, 1)), isoLocal(now)];
  return null;
}

export default function Pnl() {
  const { branchFilter } = useAuth();
  const navigate = useNavigate();
  const [preset, setPreset] = useState("mes");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [range, setRange] = useState(null);
  const [expandedCats, setExpandedCats] = useState({});
  const [catDetails, setCatDetails] = useState({});

  const load = useCallback(() => {
    let range = presetRange(preset);
    if (preset === "custom") {
      if (!custom.from || !custom.to) { setData(null); return; }
      range = [custom.from, custom.to];
    }
    const p = new URLSearchParams({ dfrom: range[0], dto: range[1] });
    if (branchFilter) p.set("branch_id", branchFilter);
    setRange(range);
    setCatDetails({});
    api.get(`/pnl?${p}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [preset, custom, branchFilter]);

  useEffect(() => { load(); }, [load]);

  const v = data?.ventas || {};
  const g = data?.gastos || { por_grupo: [], por_categoria: [] };
  const res = data?.resultado || {};
  const iva = data?.iva || {};
  const cobros = data?.cobros || {};
  const catsByGroup = {};
  (g.por_categoria || []).forEach((c) => {
    (catsByGroup[c.group] = catsByGroup[c.group] || []).push(c);
  });
  const pctOf = (val) => v.net_sales ? `${Math.round((val / v.net_sales) * 1000) / 10}%` : "—";
  const fmtD = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
  const toggleCat = (cid) => {
    const open = !expandedCats[cid];
    setExpandedCats((s) => ({ ...s, [cid]: open }));
    if (open && !catDetails[cid] && range) {
      const p = new URLSearchParams({ category_id: cid, dfrom: range[0], dto: range[1] });
      if (branchFilter) p.set("branch_id", branchFilter);
      api.get(`/pnl/category-detail?${p}`)
        .then((r) => setCatDetails((s) => ({ ...s, [cid]: r.data.items })))
        .catch(() => setCatDetails((s) => ({ ...s, [cid]: [] })));
    }
  };

  return (
    <div className="space-y-6" data-testid="pnl-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">P&L — Cuenta de resultados</h1>
          <p className="text-sm text-slate-500 mt-1">Ventas Loyverse netas + otros ingresos − gastos por devengo (facturas + gastos bancarios sin factura)</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <Select value="" onValueChange={(ym) => { const [f, t] = monthRangeFor(ym); setPreset("custom"); setCustom({ from: f, to: t }); }}>
            <SelectTrigger data-testid="pnl-month-filter" className="w-44 h-9"><SelectValue placeholder="Mes concreto..." /></SelectTrigger>
            <SelectContent>
              {monthOptions().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger data-testid="pnl-period-select" className="w-48 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mes">Mes actual</SelectItem>
              <SelectItem value="mes_anterior">Mes anterior</SelectItem>
              <SelectItem value="3m">Últimos 3 meses</SelectItem>
              <SelectItem value="ytd">Año en curso</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
          {preset === "custom" && (
            <>
              <input data-testid="pnl-date-from" type="date" value={custom.from} className="h-9 border rounded-md px-2 text-sm"
                onChange={(e) => setCustom({ ...custom, from: e.target.value })} />
              <input data-testid="pnl-date-to" type="date" value={custom.to} className="h-9 border rounded-md px-2 text-sm"
                onChange={(e) => setCustom({ ...custom, to: e.target.value })} />
            </>
          )}
        </div>
      </div>

      {!data ? (
        <div className="text-sm text-slate-400 py-10 text-center">Cargando P&L...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Ventas netas", value: fmtEUR(v.net_sales ?? 0), icon: TrendingUp, testid: "pnl-kpi-ventas", hint: `${fmtNum(v.tickets ?? 0)} tickets · sin IVA/propinas` },
              { label: "Gastos", value: fmtEUR(g.total ?? 0), icon: TrendingDown, testid: "pnl-kpi-gastos", hint: "facturas + banco, netos de IVA" },
              { label: "Resultado neto", value: fmtEUR(res.resultado_neto ?? 0), icon: Wallet, testid: "pnl-kpi-resultado", hint: null, color: (res.resultado_neto ?? 0) >= 0 ? "text-green-700" : "text-red-700" },
              { label: "Margen", value: res.margen_pct !== null && res.margen_pct !== undefined ? `${res.margen_pct}%` : "—", icon: Percent, testid: "pnl-kpi-margen", hint: `Margen bruto: ${fmtEUR(res.margen_bruto ?? 0)}` },
            ].map((c) => (
              <Card key={c.testid} data-testid={c.testid}>
                <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">{c.label}</CardTitle>
                  <c.icon className="w-4 h-4 text-slate-400" />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-extrabold font-num tracking-tight ${c.color || ""}`}>{c.value}</div>
                  {c.hint && <div className="text-xs text-slate-400 mt-1">{c.hint}</div>}
                </CardContent>
              </Card>
            ))}
          </div>

          <Card data-testid="pnl-expenses-card">
            <CardHeader><CardTitle className="text-base font-semibold font-heading">Desglose de gastos</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table data-testid="pnl-expenses-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Categoría</TableHead>
                    <TableHead className="text-right">Facturas</TableHead>
                    <TableHead className="text-right">Banco (sin factura)</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">% ventas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-slate-50 font-semibold" data-testid="pnl-row-ventas">
                    <TableCell>Ventas netas (Loyverse)</TableCell>
                    <TableCell></TableCell><TableCell></TableCell>
                    <TableCell className="text-right font-num text-green-700">{fmtEUR(v.net_sales ?? 0)}</TableCell>
                    <TableCell className="text-right font-num text-sm text-slate-500">{v.net_sales ? "100%" : "—"}</TableCell>
                  </TableRow>
                  {(data.otros_ingresos?.por_categoria || []).length > 0 && (
                    <Fragment>
                      <TableRow className="bg-slate-50/60" data-testid="pnl-income-group-otros_ingresos">
                        <TableCell className="font-semibold">Otros ingresos</TableCell>
                        <TableCell></TableCell><TableCell></TableCell>
                        <TableCell className="text-right font-num font-semibold text-green-700">{fmtEUR(data.otros_ingresos.total)}</TableCell>
                        <TableCell className="text-right font-num text-sm text-slate-500">{pctOf(data.otros_ingresos.total)}</TableCell>
                      </TableRow>
                      {data.otros_ingresos.por_categoria.map((c) => (
                        <TableRow key={c.category_id} data-testid={`pnl-cat-${c.category_id}`}>
                          <TableCell className="pl-8 text-sm text-slate-600">{c.name}</TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right font-num text-sm">{fmtEUR(c.total)}</TableCell>
                          <TableCell className="text-right font-num text-sm">{fmtEUR(c.total)}</TableCell>
                          <TableCell className="text-right font-num text-sm text-slate-500">{pctOf(c.total)}</TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  )}
                  {(g.por_grupo || []).map((gr) => (
                    <Fragment key={gr.group}>
                      <TableRow className="bg-slate-50/60" data-testid={`pnl-group-${gr.group}`}>
                        <TableCell className="font-semibold">{GROUP_LABELS[gr.group] || gr.group}</TableCell>
                        <TableCell></TableCell><TableCell></TableCell>
                        <TableCell className="text-right font-num font-semibold text-red-700">−{fmtEUR(gr.total)}</TableCell>
                        <TableCell className="text-right font-num text-sm text-slate-500">{pctOf(gr.total)}</TableCell>
                      </TableRow>
                      {(catsByGroup[gr.group] || []).map((c) => (
                        <Fragment key={c.category_id}>
                          <TableRow data-testid={`pnl-cat-${c.category_id}`}>
                            <TableCell className="pl-8 text-sm text-slate-600">
                              <button type="button" data-testid={`pnl-cat-toggle-${c.category_id}`}
                                onClick={() => toggleCat(c.category_id)}
                                className="inline-flex items-center gap-1 hover:text-slate-900 transition-colors cursor-pointer">
                                {expandedCats[c.category_id]
                                  ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                                  : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                                {c.name}
                              </button>
                              {c.n_facturas > 0 && <span className="text-xs text-slate-400 ml-2">{c.n_facturas} fact.</span>}
                            </TableCell>
                            <TableCell className="text-right font-num text-sm">{c.facturas ? fmtEUR(c.facturas) : "—"}</TableCell>
                            <TableCell className="text-right font-num text-sm">{c.banco ? fmtEUR(c.banco) : "—"}</TableCell>
                            <TableCell className="text-right font-num text-sm">{fmtEUR(c.total)}</TableCell>
                            <TableCell className="text-right font-num text-sm text-slate-500">{pctOf(c.total)}</TableCell>
                          </TableRow>
                          {expandedCats[c.category_id] && (
                            !catDetails[c.category_id] ? (
                              <TableRow><TableCell colSpan={5} className="pl-14 text-xs text-slate-400">Cargando detalle...</TableCell></TableRow>
                            ) : catDetails[c.category_id].length === 0 ? (
                              <TableRow><TableCell colSpan={5} className="pl-14 text-xs text-slate-400">Sin movimientos en el periodo.</TableCell></TableRow>
                            ) : catDetails[c.category_id].map((it) => (
                              <TableRow key={`${c.category_id}-${it.kind}-${it.id}`} className="bg-slate-50/40 cursor-pointer hover:bg-slate-100 transition-colors"
                                data-testid={`pnl-detail-${c.category_id}-${it.id}`}
                                title={it.kind === "factura" ? "Ver factura" : it.kind === "nomina" ? "Ver nómina en Personal" : "Ver movimiento en conciliación"}
                                onClick={() => navigate(it.kind === "factura" ? `/facturas?invoice=${it.id}` : it.kind === "nomina" ? "/personal" : `/conciliacion?tx=${it.id}`)}>
                                <TableCell className="pl-14 text-xs text-slate-500">
                                  <span className="text-slate-400 mr-2">{it.kind === "factura" ? "Factura" : it.kind === "nomina" ? "Nómina" : "Banco"}</span>
                                  <span className="font-num mr-2">{fmtD(it.date)}</span>
                                  {it.label}
                                </TableCell>
                                <TableCell className="text-right font-num text-xs text-slate-500">{it.kind !== "banco" ? fmtEUR(it.amount) : ""}</TableCell>
                                <TableCell className="text-right font-num text-xs text-slate-500">{it.kind === "banco" ? fmtEUR(it.amount) : ""}</TableCell>
                                <TableCell className="text-right font-num text-xs text-slate-500">{fmtEUR(it.amount)}</TableCell>
                                <TableCell className="text-right font-num text-xs text-slate-400">{pctOf(it.amount)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </Fragment>
                      ))}
                    </Fragment>
                  ))}
                  <TableRow className="border-t-2 font-bold" data-testid="pnl-row-resultado">
                    <TableCell>Resultado neto</TableCell>
                    <TableCell></TableCell><TableCell></TableCell>
                    <TableCell className={`text-right font-num ${(res.resultado_neto ?? 0) >= 0 ? "text-green-700" : "text-red-700"}`}>
                      {fmtEUR(res.resultado_neto ?? 0)}
                    </TableCell>
                    <TableCell className="text-right font-num text-sm text-slate-500">{pctOf(res.resultado_neto ?? 0)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="pnl-iva-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base font-semibold font-heading">Balance de IVA</CardTitle>
                <Landmark className="w-4 h-4 text-slate-400" />
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">IVA repercutido (ventas)</span><span className="font-num font-semibold">{fmtEUR(iva.repercutido ?? 0)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">IVA soportado (facturas)</span><span className="font-num font-semibold">{(iva.soportado ?? 0) > 0 ? `−${fmtEUR(iva.soportado)}` : fmtEUR(0)}</span></div>
                {(iva.facturas_sin_desglose ?? 0) > 0 && (
                  <p className="text-xs text-amber-600" data-testid="pnl-sin-desglose-iva">
                    {iva.facturas_sin_desglose} facturas sin desglose de IVA — cuentan con IVA incluido hasta reprocesarlas
                  </p>
                )}
                <div className="flex justify-between border-t pt-2">
                  <span className="font-medium">{(iva.balance ?? 0) >= 0 ? "A pagar a Hacienda" : "A compensar"}</span>
                  <span className={`font-num font-bold ${(iva.balance ?? 0) >= 0 ? "text-amber-700" : "text-green-700"}`}>{fmtEUR(iva.balance ?? 0)}</span>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="pnl-cobros-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base font-semibold font-heading">Control efectivo / tarjeta</CardTitle>
                <CreditCard className="w-4 h-4 text-slate-400" />
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Cobrado según Loyverse (con IVA)</span><span className="font-num font-semibold">{fmtEUR(cobros.loyverse_total ?? 0)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Liquidaciones de tarjeta en banco</span><span className="font-num font-semibold">{fmtEUR(cobros.banco_tarjeta ?? 0)}</span></div>
                {(cobros.otros_ingresos_marcados ?? 0) > 0 && (
                  <div className="flex justify-between"><span className="text-slate-500">Otros ingresos marcados Loyverse</span><span className="font-num">{fmtEUR(cobros.otros_ingresos_marcados)}</span></div>
                )}
                <div className="flex justify-between border-t pt-2">
                  <span className="font-medium">Efectivo estimado</span>
                  <span className="font-num font-bold">{fmtEUR(cobros.efectivo_estimado ?? 0)}</span>
                </div>
                {(cobros.banco_tarjeta ?? 0) < 1 && (
                  <Badge data-testid="pnl-cobros-warning" variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">Sin liquidaciones de tarjeta categorizadas: el efectivo estimado aún no es fiable</Badge>
                )}
                <p className="text-xs text-slate-400 pt-1">Efectivo = cobrado Loyverse − liquidaciones de tarjeta categorizadas en el banco (ambos con IVA). Marca las liquidaciones desde Conciliación.</p>
              </CardContent>
            </Card>
          </div>

          {(data.per_branch || []).length > 0 && (
            <Card data-testid="pnl-branch-card">
              <CardHeader><CardTitle className="text-base font-semibold font-heading">Por sucursal</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table data-testid="pnl-branch-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sucursal</TableHead>
                      <TableHead className="text-right">Ventas netas</TableHead>
                      <TableHead className="text-right">Gastos</TableHead>
                      <TableHead className="text-right">Resultado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.per_branch.map((b) => (
                      <TableRow key={b.branch_id} data-testid={`pnl-branch-${b.branch_id}`}>
                        <TableCell className="font-medium">{b.branch_name}</TableCell>
                        <TableCell className="text-right font-num">{fmtEUR(b.net_sales)}</TableCell>
                        <TableCell className="text-right font-num text-red-700">{b.gastos > 0 ? `−${fmtEUR(b.gastos)}` : fmtEUR(0)}</TableCell>
                        <TableCell className={`text-right font-num font-semibold ${b.resultado >= 0 ? "text-green-700" : "text-red-700"}`}>{fmtEUR(b.resultado)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
